import { Col, Row, Input } from 'antd';
import React from 'react';
import { useTranslation } from 'react-i18next';
import FormInput from '../../global/Forms/FormInput';
import FormRadio from '../../global/Forms/FormRadio';
import { DeliveryType } from '../../../utils/ChannelUtils';
import { ExtendedResources } from '../../../../types/resources';
import FormEmailTags from '../../global/Forms/FormEmailTags';
import TestFTPButton from './TestFTPButton';
import ObfuscatedSSHKeyInput from '../../global/Forms/ObfuscatedSSHKeyInput';

type DeliveryFormPartProps = {
  disabled: boolean;
  values: any;
  deliveryTypes: ExtendedResources[];
  ftpDirectoryTypes: ExtendedResources[];
  protocolTypes: ExtendedResources[];
  channelPlanId?: number;
  channelId?: number;
};

const DeliveryFormPart: React.FC<DeliveryFormPartProps> = ({
  disabled,
  values,
  deliveryTypes,
  ftpDirectoryTypes,
  // ftpModes,
  // encryptionTypes,
  protocolTypes,
  channelPlanId,
  channelId,
}) => {
  const { t } = useTranslation();

  const protocolTypeName =
    protocolTypes.find(type => type.id === values.delivery.protocolTypeId)?.code?.toLowerCase() ||
    '';

  return (
    <React.Fragment>
      <Row gutter={20}>
        <Col span={10}>
          <FormRadio
            name="delivery.deliveryTypeId"
            values={deliveryTypes.map(type => {
              const frequencyId = values.schedule
                ? values.schedule.deliveryFrequencyId
                : values.deliveryFrequencyId;
              return type.code === 'manual' && frequencyId !== 4
                ? { ...type, disabled: true }
                : type;
            })}
            label={t('channel:deliveryMethod')}
            disabled={disabled}
            required
          />
        </Col>
      </Row>
      {values.delivery.deliveryTypeId === DeliveryType.FTP && (
        <React.Fragment>
          <Row gutter={20}>
            <Col span={8}>
              <FormInput
                addonBefore={values.delivery.protocolTypeId && `${protocolTypeName}://`}
                name="delivery.server"
                label={t('channel:server')}
                placeholder={t('channel:sampleserver')}
                required
                disabled={disabled}
                testId="server"
              />
            </Col>
            <Col span={7}>
              <FormInput
                name="delivery.user"
                label={t('channel:user')}
                placeholder={t('channel:username')}
                required
                disabled={disabled}
              />
            </Col>
            <Col span={2}>
              <FormInput
                name="delivery.port"
                label={t('channel:port')}
                placeholder={t('channel:sampleport')}
                disabled={disabled}
              />
            </Col>
          </Row>
          <Row gutter={20}>
            <Col span={24}>
              <FormRadio
                name="delivery.authMethod"
                values={[
                  { id: 'password', name: t('channel:passwordAuth') },
                  { id: 'ssh_key', name: t('channel:sshKeyAuth') },
                ]}
                label={t('channel:authenticationMethod')}
                required
                disabled={disabled}
              />
            </Col>
          </Row>
          <Row gutter={20}>
            <Col span={24}>
              {values.delivery.authMethod === 'password' && (
                <FormInput
                  name="delivery.password"
                  label={t('channel:password')}
                  placeholder={t('channel:password')}
                  required
                  disabled={disabled}
                  type="password"
                />
              )}
              {values.delivery.authMethod === 'ssh_key' && (
                <ObfuscatedSSHKeyInput
                  name="delivery.sshPrivateKey"
                  label={t('channel:sshPrivateKey')}
                  placeholder={t('channel:sshKeyPlaceholder')}
                  required
                  disabled={disabled}
                  rows={8}
                />
              )}
            </Col>
          </Row>
          <div className="flex">
            <FormRadio
              name="delivery.ftpDirectoryTypeId"
              values={ftpDirectoryTypes}
              label={t('channel:folder')}
              required
              disabled={disabled}
            />
            <div className="flex flex-1 items-end">
              {values.delivery.ftpDirectoryTypeId === 1 && (
                <Input
                  className="flex-1 channel__ftp-path"
                  value={`${protocolTypeName}://${values.delivery.server || ''}`}
                  disabled
                />
              )}
              {values.delivery.ftpDirectoryTypeId === 2 && (
                <Input
                  className="flex-1 channel__ftp-path"
                  value={`${protocolTypeName}://${values.delivery.server || ''}/<brand>`}
                  disabled
                />
              )}
              {values.delivery.ftpDirectoryTypeId === 3 && (
                <FormInput
                  className="flex-1 channel__ftp-folder"
                  name="delivery.customDirectory"
                  placeholder={t('channel:sampleFolder')}
                  disabled={disabled || values.delivery.ftpDirectoryTypeId !== 3}
                  addonBefore={`${protocolTypeName}://${values.delivery.server || ''}/`}
                />
              )}
            </div>
          </div>
          <Row gutter={20}>
            {/* <Col span={5}>
            <FormRadio
              name="delivery.ftpModeId"
              values={ftpModes}
              label={t('channel:mode')}
              required
              disabled={disabled}
            />
          </Col> */}
            <Col span={4}>
              <FormRadio
                name="delivery.protocolTypeId"
                values={protocolTypes}
                label={t('channel:protocol')}
                required
                disabled={disabled}
              />
            </Col>
            <Col span={20}>
              {/* <FormRadio
                name="delivery.ftpEncryptionTypeId"
                values={encryptionTypes}
                label={t('channel:encryption')}
                required
                disabled={disabled}
              /> */}
              <TestFTPButton
                server={values.delivery.server}
                userName={values.delivery.user}
                password={values.delivery.password}
                port={values.delivery.port}
                customDirectory={values.delivery.customDirectory}
                protocolTypeId={values.delivery.protocolTypeId}
                authMethod={values.delivery.authMethod}
                sshPrivateKey={values.delivery.sshPrivateKey}
                channelPlanId={channelPlanId}
                channelId={channelId}
              />
            </Col>
          </Row>
        </React.Fragment>
      )}
      {values.delivery.deliveryTypeId === DeliveryType.EMAIL && (
        <Row>
          <Col span={24}>
            <FormEmailTags
              name="delivery.email"
              label={t('channel:emails')}
              placeholder={t('channel:emails')}
              required
              disabled={disabled}
            />
          </Col>
        </Row>
      )}
    </React.Fragment>
  );
};

export default DeliveryFormPart;
